import { useQuery } from "@tanstack/react-query";

import { enviroment } from "../../enviroment";
import { DesignDto } from "../../data/models/responses/DesignDto";
import { LogoDto } from "../../data/models/responses/LogoDto";

export function usePublicApiClient() {
  const API_URL = enviroment.apiUrl;

  function getDesignCache(designId: number, code: string) {
    return useQuery(["ext-design", designId, code], () =>
      getDesign(designId, code)
    );
  }

  async function getDesign(designId: number, code: string): Promise<DesignDto> {
    const response = await fetch(
      `${API_URL}/organizations/designs/${designId}/publickey/${code}`
    );
    const data = await response.json();
    //console.log("getDesign: ", data);
    return data;
  }

  function getLogoCache(logoId: number, code: string) {
    return useQuery(["ext-logo", logoId, code], () => getLogo(logoId, code));
  }

  async function getLogo(logoId: number, code: string): Promise<LogoDto> {
    const response = await fetch(
      `${API_URL}/organizations/logos/${logoId}/publickey/${code}`
    );
    const data = await response.json();
    //console.log("getLogo: ", data);
    return data;
  }

  async function placeVerdictLogo(
    logoId: number,
    approval: boolean,
    rejectionNote: string | undefined,
    code: string
  ): Promise<LogoDto> {
    const body: any = {
      approve: approval,
      code: code,
    };

    if (rejectionNote !== undefined) {
      body.rejectionNote = rejectionNote;
    }

    const response = await fetch(
      `${API_URL}/organizations/logos/${logoId}/approval-external`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async function placeVerdictDesign(
    designId: number,
    approval: boolean,
    rejectionNote: string | undefined,
    code: string
  ): Promise<DesignDto> {
    const body: any = {
      approve: approval,
      code: code,
    };

    if (rejectionNote !== undefined) {
      body.rejectionNote = rejectionNote;
    }

    const response = await fetch(
      `${API_URL}/organizations/designs/${designId}/approval-external`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async function getMultipleLogos(keys: string[]): Promise<LogoDto[]> {
    const keysParam = keys.join(",");
    const response = await fetch(`${API_URL}/approval/logos?keys=${keysParam}`);
    const data = await response.json();
    //console.log("getMultipleLogos: ", data);
    return data;
  }

  async function getMultipleDesigns(keys: string[]): Promise<DesignDto[]> {
    const keysParam = keys.join(",");
    const response = await fetch(
      `${API_URL}/approval/designs?keys=${keysParam}`
    );
    const data = await response.json();
    //console.log("getMultipleDesigns: ", data);
    return data;
  }

  async function getLogosForPdf(keys: string[]): Promise<LogoDto[]> {
    const keysParam = keys.join(",");
    const response = await fetch(
      `${API_URL}/approval/logos/pdf-data?keys=${keysParam}`
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF data for logos: ${response.status}`);
    }
    const data = await response.json();
    //console.log("getLogosForPdf: ", data);
    return data;
  }

  async function getDesignsForPdf(keys: string[]): Promise<DesignDto[]> {
    const keysParam = keys.join(",");
    const response = await fetch(
      `${API_URL}/approval/designs/pdf-data?keys=${keysParam}`
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch PDF data for designs: ${response.status}`
      );
    }
    const data = await response.json();
    //console.log("getDesignsForPdf: ", data);
    return data;
  }

  return {
    getLogo: getLogoCache,
    getDesign: getDesignCache,
    placeVerdictLogo,
    placeVerdictDesign,
    getMultipleLogos,
    getMultipleDesigns,
    getLogosForPdf,
    getDesignsForPdf,
  };
}
